import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import "./Dropdown.css";

function Dropdown({ options = [], value = "", onChange, name, id, placeholder = "Select", disabled }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const normalized = useMemo(() => {
    return (Array.isArray(options) ? options : []).map((o) => ({
      label: o.label ?? String(o.value ?? o.id ?? ""),
      value: o.value ?? o.id ?? String(o.label ?? ""),
    }));
  }, [options]);

  const selected = normalized.find((o) => String(o.value) === String(value));

  const fireChange = (newValue) => {
    if (typeof onChange === "function") {
      onChange({ target: { name, value: newValue } });
    }
  };

  const toggle = () => !disabled && setOpen((p) => !p);
  const close = () => setOpen(false);

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // keyboard interactions
  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(Math.max(0, normalized.findIndex((o) => String(o.value) === String(value))));
      return;
    }
    if (!open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % normalized.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + normalized.length) % normalized.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = normalized[activeIndex];
      if (opt) {
        fireChange(opt.value);
        close();
      }
    }
  };

  const onOptionClick = (opt, idx) => {
    fireChange(opt.value);
    setActiveIndex(idx);
    close();
  };

  return (
    <div className={`dd-wrapper ${disabled ? "dd-disabled" : ""}`}> 
      <button
        ref={btnRef}
        type="button"
        id={id}
        name={name}
        className={`dd-btn ${selected ? "selected" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={onKeyDown}
        disabled={disabled}
      >
        <span className={`dd-label ${selected ? "has-value" : ""}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={16} />
      </button>

      {open && (
        <ul
          className="dd-menu"
          role="listbox"
          aria-activedescendant={activeIndex > -1 ? `opt-${activeIndex}` : undefined}
          tabIndex={-1}
          ref={menuRef}
          onKeyDown={onKeyDown}
        >
          {normalized.map((opt, idx) => (
            <li
              id={`opt-${idx}`}
              key={opt.value}
              role="option"
              aria-selected={String(opt.value) === String(value)}
              className={`dd-option ${idx === activeIndex ? "active" : ""} ${String(opt.value) === String(value) ? "selected" : ""}`}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => onOptionClick(opt, idx)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default Dropdown;